import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Divider, CircularProgress, Alert, Button } from '@mui/material';
import Navbar from '../Layouts/Navbar';
import { Person } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';


export default function UtilisateursDetails() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Fetch user data when component mounts or location changes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Get userId from URL path params
        const userId = location.pathname.split('/').pop();
        
        if (!userId) {
          setError('ID utilisateur non spécifié');
          setLoading(false);
          return;
        }
        
        // Fetch user data from API
        const response = await api.get(`/users/${userId}`);
        const userData = response.data;
        
        // If user has site data with ID_Societe, fetch societe data
        if (userData.site && userData.site.ID_Societe) {
          try {
            const societeResponse = await api.get(`/societes/${userData.site.ID_Societe}`);
            // Update user data with societe information
            userData.societe = societeResponse.data;
          } catch (societeErr) {
            console.error('Error fetching societe details:', societeErr);
            // Continue without societe data
          }
        }
        
        setUserData(userData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError(err.response?.data?.message || 'Erreur lors du chargement des données utilisateur');
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [location]);
  
  // Handle redirect when error is set to ID missing
  useEffect(() => {
    let redirectTimer;
    if (error === 'ID utilisateur non spécifié') {
      redirectTimer = setTimeout(() => {
        navigate('/utilisateurs');
      }, 3000);
    }
    
    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [error, navigate]);
  
  const handleBack = () => {
    navigate('/utilisateurs');
  };
  
  return (
    <>
      <Navbar />
      <Box p={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Person />
            <Typography variant="h6" ml={1}> DETAIL D'UTILISATEUR</Typography>
          </Box>
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                cursor: 'pointer', 
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' } 
              }}
              onClick={handleBack}
            >
              Retour à la liste
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box mt={4}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress color="primary" />
            </Box>
          ) : error ? (
            <Alert severity="error">
              {error}
              {error === 'ID utilisateur non spécifié' && (
                <Typography variant="body2" mt={1}>
                  Redirection vers la liste des utilisateurs dans quelques secondes...
                </Typography>
              )}
            </Alert>
          ) : userData ? (
            <Grid container spacing={2}>
              {/* Right Section */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent='end'>
                  <Box display="flex" flexDirection="column" width="50%">
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Société:</Typography>
                      <Typography variant="body2" textAlign="center">{userData.societe?.name || (userData.site?.societe?.name) || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Matricule:</Typography>
                      <Typography variant="body2" textAlign="center">{userData.matricule || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Nom:</Typography>
                      <Typography variant="body2" textAlign="center">{userData.name || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Prénom:</Typography>
                      <Typography variant="body2" textAlign="center">{userData.prenom || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Rôle:</Typography>
                      <Typography variant="body2" textAlign="center">{userData.role?.Role || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Statut:</Typography>
                      <Typography 
                        variant="body2" 
                        textAlign="center"
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: '4px',
                          backgroundColor:userData.Status === 'actif' || userData.Status === 'Actif' || userData.Status === true || userData.Status === 1 ? '#e6f7e6' : '#ffebee',
                          color:userData.Status === 'actif' || userData.Status === 'Actif' || userData.Status === true || userData.Status === 1 ? '#2e7d32' : '#c62828',
                        }}
                      >
                        {userData.Status === 'actif' || userData.Status === 'Actif' || userData.Status === true || userData.Status === 1 ? 'Actif' : 'Inactif'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Left Section */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent='start'>
                  <Box display="flex" flexDirection="column" width="50%">
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Site:</Typography>
                      <Typography variant="body2">{userData.site?.name || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Email:</Typography>
                      <Typography variant="body2">{userData.email || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Téléphone:</Typography>
                      <Typography variant="body2">{userData.telephone || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Zone de ramassage:</Typography>
                      <Typography variant="body2">{userData.pickup?.name || 'N/A'}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body1" fontWeight='bold'>Coordonnées GPS:</Typography>
                      <Typography variant="body2">
                        {userData.latitude && userData.longitude ? 
                          `${userData.latitude}, ${userData.longitude}` : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              {/* Map Section */}
              <Grid item xs={12} mt={3}>
                <Typography variant="h6" mb={2}>Localisation</Typography>
                {(userData.latitude && userData.longitude) ? (
                  <>
                    <Box sx={{ width: '100%', height: '400px', border: '1px solid #ddd', borderRadius: '4px', position: 'relative' }}>
                      <iframe
                        title="User Location"
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        src={`https://maps.google.com/maps?q=${userData.latitude},${userData.longitude}&z=15&output=embed`}
                        allowFullScreen
                        onLoad={() => setMapLoading(false)}
                        onError={() => {
                          console.error('Failed to load map');
                          setMapLoading(false);
                          setError('Erreur lors du chargement de la carte');
                        }}
                      />
                      {mapLoading && (
                        <Box 
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 1,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      )}
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Coordonnées: {userData.latitude}, {userData.longitude}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="primary" 
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${userData.latitude},${userData.longitude}`, '_blank')}
                      >
                        Ouvrir dans Google Maps
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box 
                    sx={{ 
                      width: '100%', 
                      height: '100px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Aucune coordonnée GPS disponible pour cet utilisateur
                    </Typography>
                  </Box>
                )}
                
                {userData.adresse && (
                  <Typography variant="body2" mt={2}>
                    <strong>Adresse:</strong> {userData.adresse}
                  </Typography>
                )}
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">Aucune donnée utilisateur trouvée</Alert>
          )}
        </Box>
      </Box>
    </>
  );
}